/* Kashira recently viewed — device-local, max 8 handles.
   <kashira-recently-viewed data-current="handle"> records the current
   product and renders the rest as cards (hidden entirely when empty). */
(() => {
  const KEY = 'kashira:recently-viewed';
  const MAX = 8;

  const list = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  };

  const formatINR = (paise) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(paise / 100);

  if (customElements.get('kashira-recently-viewed')) return;
  customElements.define(
    'kashira-recently-viewed',
    class extends HTMLElement {
      connectedCallback() {
        const current = this.dataset.current;
        const previous = list().filter((h) => h !== current);

        if (current) {
          localStorage.setItem(KEY, JSON.stringify([current, ...previous].slice(0, MAX)));
        }
        if (!previous.length) return;

        const handles = previous.slice(0, this.dataset.max || 4);

        // Reserve the 3:4 frames up front (zero CLS, spec 03) and reveal the
        // section before paint, then defer the network work until the row is
        // within 200px of the viewport ("recos row lazy-rendered at 200px").
        this.querySelector('.kashira-rv__grid').innerHTML = handles
          .map(() => '<li class="kashira-rv__item"><span class="kashira-rv__media"></span></li>')
          .join('');
        this.hidden = false;

        if (!('IntersectionObserver' in window)) {
          this.render(handles);
          return;
        }
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            observer.disconnect();
            this.render(handles);
          },
          { rootMargin: '0px 0px 200px 0px' }
        );
        observer.observe(this);
      }

      async render(handles) {
        const products = (
          await Promise.all(
            handles.map((h) => fetch(`/products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null))
          )
        ).filter(Boolean);
        if (!products.length) {
          // Nothing resolved — drop the reserved frames again rather than
          // leaving empty placeholders on the page.
          this.querySelector('.kashira-rv__grid').innerHTML = '';
          this.hidden = true;
          return;
        }

        this.querySelector('.kashira-rv__grid').innerHTML = products
          .map(
            (p, index) => `
          <li class="kashira-rv__item scroll-trigger animate--slide-in" data-cascade style="--animation-order: ${index};">
            <!--
              aria-hidden + tabindex="-1": this link holds only a decorative
              image, so on its own it was a tab stop that announced just
              "link" with no name (WCAG 2.4.4 / 4.1.2). The title link below
              points at the same URL, so hiding this one from the a11y tree
              gives every product exactly one named tab stop instead of one
              named and one silent. The image stays clickable by mouse.
            -->
            <a href="${p.url}" class="kashira-rv__media" aria-hidden="true" tabindex="-1">
              ${p.featured_image ? `<img src="${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=480" alt="" loading="lazy" width="480" height="640">` : ''}
            </a>
            <a href="${p.url}" class="kashira-rv__title">${p.title}</a>
            <p class="kashira-rv__price">${formatINR(p.price)}</p>
          </li>`
          )
          .join('');
        this.hidden = false;

        // Cards land after animations.js has run: park them offscreen and
        // re-observe so they cascade in (40ms stagger) at 18% of the viewport.
        // Under reduced motion the scroll-trigger rules are inert, so the cards
        // are simply present with no movement.
        if (typeof initializeScrollAnimationTrigger === 'function') {
          this.querySelectorAll('.scroll-trigger').forEach((element) => {
            element.classList.add('scroll-trigger--offscreen');
          });
          initializeScrollAnimationTrigger(this);
        }
      }
    }
  );
})();
